import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CategoryIcon from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import SpaIcon from '@mui/icons-material/Spa';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import { OfferStructuredFacilityRow } from '~/components/Offers/configs/structuredFacilityAssociationConfig';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import {
  useGetApprovedStructuredFacilities,
  useGetMappedProperty,
  useGetOfferStructuredFacilities,
  useImportOfferStructuredFacilities,
  useSaveOfferStructuredFacilities,
} from '~/hooks/apis/offers/useOfferStructuredFacilities';

import * as AccommodationService from '~/services/AccommodationService';

import { getHotelUniquePropertyIds } from '~/utils/hotelUtils';

const ORDER_STEP = 10;

interface StructuredFacilitiesEditorProps {
  offerId: string;
  invalidateOfferDetailOnSave?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onDraftChange?: (rows: Array<OfferStructuredFacilityRow>) => void;
  showActions?: boolean;
}

interface FacilityGroupView {
  group: AccommodationService.AdminCatalogGroup;
  facilities: Array<OfferStructuredFacilityRow>;
}

interface DeletedFacilitiesState {
  anchorGroupCode?: string;
  facilityIndex?: number;
  fallbackIndex: number;
  message: string;
  groups: Array<FacilityGroupView>;
}

function getGroupSortableId(groupCode: string) {
  return `group:${groupCode}`;
}

function getFacilitySortableId(facilityCode: string) {
  return `facility:${facilityCode}`;
}

function getCodeFromSortableId(id: string) {
  return id.split(':')[1];
}

function cloneGroups(groups: Array<FacilityGroupView>) {
  return groups.map((group) => ({
    group: { ...group.group },
    facilities: group.facilities.map((facility) => ({
      ...facility,
      catalogGroup: facility.catalogGroup ? { ...facility.catalogGroup } : facility.catalogGroup,
    })),
  }));
}

function normalizeFacilityOrders(facilities: Array<OfferStructuredFacilityRow>) {
  return facilities.map((facility, index) => ({
    ...facility,
    displayOrder: facility.displayOrder ?? (index + 1) * ORDER_STEP,
  }));
}

function normalizeGroupOrders(groups: Array<FacilityGroupView>) {
  return groups.map((group, index) => {
    const displayOrder = (index + 1) * ORDER_STEP;
    const updatedGroup = {
      ...group.group,
      displayOrder,
    };

    return {
      group: updatedGroup,
      facilities: normalizeFacilityOrders(group.facilities).map((facility) => ({
        ...facility,
        catalogGroup: updatedGroup,
      })),
    };
  });
}

function rowsToGroups(rows: Array<OfferStructuredFacilityRow>) {
  const groupsByCode = new Map<string, FacilityGroupView>();

  rows.forEach((row) => {
    if (!row.catalogGroup) {
      return;
    }

    const existingGroup = groupsByCode.get(row.catalogGroup.code);

    if (existingGroup) {
      existingGroup.facilities.push(row);
      return;
    }

    groupsByCode.set(row.catalogGroup.code, {
      group: row.catalogGroup,
      facilities: [row],
    });
  });

  return Array.from(groupsByCode.values());
}

function groupsToRows(groups: Array<FacilityGroupView>) {
  return normalizeGroupOrders(groups).flatMap((group) => group.facilities);
}

function serializeFacilityGroups(groups: Array<FacilityGroupView>) {
  return JSON.stringify(
    groups.map((group) => ({
      code: group.group.code,
      name: group.group.name,
      facilities: group.facilities.map((facility) => ({
        code: facility.code,
        name: facility.name,
      })),
    })),
  );
}

export function hasFacilityChanges(
  groups: Array<FacilityGroupView>,
  savedRows: Array<OfferStructuredFacilityRow> = [],
) {
  return serializeFacilityGroups(groups) !== serializeFacilityGroups(rowsToGroups(savedRows));
}

function buildFacilityRow(
  group: AccommodationService.AdminCatalogGroup,
  facility: AccommodationService.AdminFacility,
  currentFacilities: Array<OfferStructuredFacilityRow>,
) {
  const nextDisplayOrder =
    currentFacilities.reduce((maxDisplayOrder, item) => {
      return Math.max(maxDisplayOrder, item.displayOrder ?? 0);
    }, 0) + ORDER_STEP;

  return {
    ...facility,
    catalogGroup: group,
    displayOrder: nextDisplayOrder,
    defaultDisplayOrder: facility.displayOrder ?? undefined,
    defaultFeatured: Boolean(facility.featured),
    featured: null,
  };
}

function resetFacilityOrdersByPosition(facilities: Array<OfferStructuredFacilityRow>) {
  return facilities.map((facility, index) => ({
    ...facility,
    displayOrder: (index + 1) * ORDER_STEP,
  }));
}

function resetGroupOrdersByPosition(groups: Array<FacilityGroupView>) {
  return groups.map((group, index) => {
    const displayOrder = (index + 1) * ORDER_STEP;
    const updatedGroup = {
      ...group.group,
      displayOrder,
    };

    return {
      group: updatedGroup,
      facilities: group.facilities.map((facility) => ({
        ...facility,
        catalogGroup: updatedGroup,
      })),
    };
  });
}

function getGroupIcon(groupCode: string) {
  if (groupCode.includes('DINING') || groupCode.includes('FOOD')) {
    return RestaurantIcon;
  }

  if (groupCode.includes('WELLNESS') || groupCode.includes('SPA')) {
    return SpaIcon;
  }

  if (groupCode.includes('PARKING') || groupCode.includes('TRANSPORT')) {
    return LocalParkingIcon;
  }

  if (groupCode.includes('GUEST')) {
    return RoomServiceIcon;
  }

  return CategoryIcon;
}

function SortableGroup({
  group,
  isCollapsed,
  onToggleCollapsed,
  onDeleteGroup,
  onDeleteFacility,
  deletedFacilitiesState,
  onDismissDelete,
  onUndoDelete,
  onUpdateGroupName,
  onUpdateFacilityName,
  availableFacilities,
  onAddFacility,
}: {
  group: FacilityGroupView;
  isCollapsed: boolean;
  onToggleCollapsed: (groupCode: string) => void;
  onDeleteGroup: (groupCode: string) => void;
  onDeleteFacility: (facilityCode: string) => void;
  deletedFacilitiesState: DeletedFacilitiesState | null;
  onDismissDelete: () => void;
  onUndoDelete: () => void;
  onUpdateGroupName: (groupCode: string, name: string) => void;
  onUpdateFacilityName: (facilityCode: string, name: string) => void;
  availableFacilities: Array<AccommodationService.AdminFacility>;
  onAddFacility: (groupCode: string, facility: AccommodationService.AdminFacility) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: getGroupSortableId(group.group.code),
  });
  const Icon = getGroupIcon(group.group.code);
  const shouldRenderDeletedFacility =
    deletedFacilitiesState?.anchorGroupCode === group.group.code &&
    typeof deletedFacilitiesState.facilityIndex === 'number';

  const renderDeletedFacilityRow = () => {
    if (!shouldRenderDeletedFacility) {
      return null;
    }

    return (
      <Box>
        <Divider />
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" startIcon={<UndoIcon />} onClick={onUndoDelete}>
                Undo
              </Button>
              <IconButton color="inherit" size="small" onClick={onDismissDelete}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
          sx={{ borderRadius: 0 }}
        >
          {deletedFacilitiesState.message}
        </Alert>
      </Box>
    );
  };

  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      sx={{
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ bgcolor: 'grey.50', px: 1, py: 1 }}>
        <IconButton size="small" {...attributes} {...listeners} aria-label={`Reorder ${group.group.name}`}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Icon fontSize="small" />
        <TextField
          aria-label={`${group.group.name} group name`}
          size="small"
          value={group.group.name}
          variant="standard"
          onChange={(event) => onUpdateGroupName(group.group.code, event.target.value)}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem', fontWeight: 600 },
          }}
          sx={{ flexGrow: 1 }}
        />
        <Tooltip title="Delete group">
          <IconButton color="error" size="small" onClick={() => onDeleteGroup(group.group.code)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => onToggleCollapsed(group.group.code)}>
          {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Stack>
      {!isCollapsed && (
        <SortableContext
          items={group.facilities.map((facility) => getFacilitySortableId(facility.code))}
          strategy={verticalListSortingStrategy}
        >
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderTop: 1,
              borderColor: 'divider',
              display: 'grid',
              gap: 1,
              gridTemplateColumns: '40px minmax(220px, 1fr) 120px 88px',
              px: 1,
              py: 0.75,
            }}
          >
            <Box />
            <Typography color="text.secondary" variant="caption">
              Display Name (Code)
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Default Rank
            </Typography>
            <Typography color="text.secondary" variant="caption">
              Actions
            </Typography>
          </Box>
          {group.facilities.map((facility, index) => (
            <React.Fragment key={facility.code}>
              {deletedFacilitiesState?.facilityIndex === index && renderDeletedFacilityRow()}
              <SortableFacilityRow
                facility={facility}
                onDeleteFacility={onDeleteFacility}
                onUpdateFacilityName={onUpdateFacilityName}
              />
            </React.Fragment>
          ))}
          {shouldRenderDeletedFacility &&
            (deletedFacilitiesState.facilityIndex ?? 0) >= group.facilities.length &&
            renderDeletedFacilityRow()}
          <AddFacilityRow
            buttonLabel="Add facility (approved only)"
            facilities={availableFacilities}
            onAddFacility={(facility) => onAddFacility(group.group.code, facility)}
          />
        </SortableContext>
      )}
    </Card>
  );
}

function AddFacilityRow({
  buttonLabel,
  facilities,
  onAddFacility,
}: {
  buttonLabel: string;
  facilities: Array<AccommodationService.AdminFacility>;
  onAddFacility: (facility: AccommodationService.AdminFacility) => void;
}) {
  const [selectedFacility, setSelectedFacility] = useState<AccommodationService.AdminFacility | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleSelectFacility = (facility: AccommodationService.AdminFacility | null) => {
    setSelectedFacility(facility);

    if (!facility) {
      return;
    }

    onAddFacility(facility);
    setSelectedFacility(null);
    setIsAdding(false);
  };

  if (!isAdding) {
    return (
      <Box>
        <Divider />
        <Box sx={{ px: 1, py: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="text"
            onClick={() => setIsAdding(true)}
            disabled={facilities.length === 0}
          >
            {buttonLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '40px minmax(220px, 1fr) 120px 88px',
          minHeight: 56,
          px: 1,
        }}
      >
        <Box />
        <Autocomplete
          autoHighlight
          autoFocus
          options={facilities}
          value={selectedFacility}
          onChange={(_event, facility) => handleSelectFacility(facility)}
          getOptionLabel={(facility) => `${facility.name} (${facility.code})`}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => <TextField {...params} label="Select approved facility" size="small" />}
        />
        <Button size="small" variant="text" onClick={() => setIsAdding(false)}>
          Cancel
        </Button>
        <Box />
      </Box>
    </Box>
  );
}

function AddGroupRow({
  groups,
  onAddGroup,
}: {
  groups: Array<AccommodationService.AdminCatalogGroup>;
  onAddGroup: (group: AccommodationService.AdminCatalogGroup) => void;
}) {
  const [selectedGroup, setSelectedGroup] = useState<AccommodationService.AdminCatalogGroup | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleSelectGroup = (group: AccommodationService.AdminCatalogGroup | null) => {
    setSelectedGroup(group);

    if (!group) {
      return;
    }

    onAddGroup(group);
    setSelectedGroup(null);
    setIsAdding(false);
  };

  if (!isAdding) {
    return (
      <Box>
        <Divider />
        <Box sx={{ px: 1, py: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="text"
            onClick={() => setIsAdding(true)}
            disabled={groups.length === 0}
          >
            Add group
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '40px minmax(220px, 1fr) 120px 88px',
          minHeight: 56,
          px: 1,
        }}
      >
        <Box />
        <Autocomplete
          autoHighlight
          autoFocus
          options={groups}
          value={selectedGroup}
          onChange={(_event, group) => handleSelectGroup(group)}
          getOptionLabel={(group) => `${group.name} (${group.code})`}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          renderInput={(params) => <TextField {...params} label="Select approved facility group" size="small" />}
        />
        <Button size="small" variant="text" onClick={() => setIsAdding(false)}>
          Cancel
        </Button>
        <Box />
      </Box>
    </Box>
  );
}

function SortableFacilityRow({
  facility,
  onDeleteFacility,
  onUpdateFacilityName,
}: {
  facility: OfferStructuredFacilityRow;
  onDeleteFacility: (facilityCode: string) => void;
  onUpdateFacilityName: (facilityCode: string, name: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: getFacilitySortableId(facility.code),
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        bgcolor: isDragging ? 'background.paper' : 'transparent',
        boxShadow: isDragging ? 3 : 'none',
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'grid',
          gap: 1,
          gridTemplateColumns: '40px minmax(220px, 1fr) 120px 88px',
          minHeight: 48,
          px: 1,
        }}
      >
        <IconButton size="small" {...attributes} {...listeners} aria-label={`Reorder ${facility.name}`}>
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <TextField
          aria-label={`${facility.name} facility name`}
          size="small"
          value={facility.name}
          variant="standard"
          onChange={(event) => onUpdateFacilityName(facility.code, event.target.value)}
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '0.875rem' },
          }}
          sx={{ flexGrow: 1 }}
        />
        <Typography color="text.secondary" variant="body2">
          {facility.defaultDisplayOrder ?? '-'}
        </Typography>
        <Box>
          <Tooltip title="Delete facility">
            <IconButton color="error" size="small" onClick={() => onDeleteFacility(facility.code)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

function CustomerPreviewDialog({
  open,
  groups,
  onClose,
}: {
  open: boolean;
  groups: Array<FacilityGroupView>;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>Customer structured facilities preview</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {groups.map((group) => {
            const Icon = getGroupIcon(group.group.code);

            return (
              <Box key={group.group.code}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Icon fontSize="small" />
                  <Typography variant="subtitle2">{group.group.name}</Typography>
                </Stack>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {group.facilities.map((facility) => (
                    <Typography key={facility.code} component="li" variant="body2">
                      {facility.name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function StructuredFacilitiesEditor({
  offerId,
  invalidateOfferDetailOnSave = true,
  onDirtyChange,
  onDraftChange,
  showActions = true,
}: StructuredFacilitiesEditorProps) {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const { data: offer } = useGetOffer(offerId);
  const [groups, setGroups] = useState<Array<FacilityGroupView>>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [collapsedGroupCodes, setCollapsedGroupCodes] = useState<Set<string>>(new Set());
  const [deletedFacilitiesState, setDeletedFacilitiesState] = useState<DeletedFacilitiesState | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const mappedPropertyId = useMemo(() => {
    if (!offer) {
      return undefined;
    }

    const propertyIds = getHotelUniquePropertyIds(offer as App.AccommodationOffer);
    return propertyIds.length === 1 ? propertyIds[0] : undefined;
  }, [offer]);
  const { data: mappedProperty } = useGetMappedProperty(mappedPropertyId);
  const mappedPropertyLabel = mappedProperty?.name ?? mappedPropertyId ?? 'No single mapped property';

  const { data: associatedFacilities, error, isFetching, isLoading } = useGetOfferStructuredFacilities(offerId);
  const { data: approvedFacilities = [] } = useGetApprovedStructuredFacilities();
  const associatedFacilityCodes = useMemo(
    () => new Set(groups.flatMap((group) => group.facilities.map((facility) => facility.code))),
    [groups],
  );
  const associatedGroupCodes = useMemo(() => new Set(groups.map((group) => group.group.code)), [groups]);
  const availableApprovedFacilities = useMemo(
    () => approvedFacilities.filter((facility) => !associatedFacilityCodes.has(facility.code)),
    [approvedFacilities, associatedFacilityCodes],
  );
  const availableApprovedFacilitiesByGroup = useMemo(() => {
    const facilitiesByGroup = new Map<string, Array<AccommodationService.AdminFacility>>();

    availableApprovedFacilities.forEach((facility) => {
      const groupCode = facility.catalogGroup?.code;

      if (!groupCode) {
        return;
      }

      const groupFacilities = facilitiesByGroup.get(groupCode) ?? [];
      facilitiesByGroup.set(groupCode, [...groupFacilities, facility]);
    });

    return facilitiesByGroup;
  }, [availableApprovedFacilities]);
  const availableApprovedGroups = useMemo(() => {
    const groupsByCode = new Map<string, AccommodationService.AdminCatalogGroup>();

    approvedFacilities.forEach((facility) => {
      const group = facility.catalogGroup;

      if (!group || associatedGroupCodes.has(group.code) || groupsByCode.has(group.code)) {
        return;
      }

      groupsByCode.set(group.code, group);
    });

    return Array.from(groupsByCode.values()).sort((first, second) => {
      const firstOrder = first.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const secondOrder = second.displayOrder ?? Number.MAX_SAFE_INTEGER;

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return first.name.localeCompare(second.name);
    });
  }, [approvedFacilities, associatedGroupCodes]);

  const importFacilitiesMutation = useImportOfferStructuredFacilities(offerId);
  const saveFacilitiesMutation = useSaveOfferStructuredFacilities(offerId, { invalidateOfferDetailOnSave });

  useEffect(() => {
    if (!associatedFacilities || isDirty) {
      return;
    }

    setGroups(normalizeGroupOrders(rowsToGroups(associatedFacilities)));
  }, [associatedFacilities, isDirty]);

  useEffect(() => {
    if (associatedFacilities && isDirty) {
      onDraftChange?.(groupsToRows(groups));
    }
  }, [associatedFacilities, groups, isDirty, onDraftChange]);

  const setNextGroups = useCallback(
    (buildGroups: (currentGroups: Array<FacilityGroupView>) => Array<FacilityGroupView>) => {
      const nextGroups = normalizeGroupOrders(buildGroups(cloneGroups(groups)));

      setGroups(nextGroups);
      setIsDirty(hasFacilityChanges(nextGroups, associatedFacilities));
    },
    [associatedFacilities, groups],
  );

  const handleImport = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Import facilities',
      description:
        'Load a replacement facilities preview from svc-accommodation. You can review and edit the preview before saving it to svc-offer.',
    });

    if (!confirmed) {
      return;
    }

    try {
      const importedFacilities = await importFacilitiesMutation.mutateAsync();

      const importedGroups = normalizeGroupOrders(rowsToGroups(importedFacilities));

      setGroups(importedGroups);
      setIsDirty(hasFacilityChanges(importedGroups, associatedFacilities));
      enqueueSnackbar('Facilities imported for review. Click Save to update the offer.', { variant: 'success' });
    } catch (importError) {
      enqueueSnackbar(`Error importing facilities: ${(importError as Error).message}`, { variant: 'error' });
    }
  }, [associatedFacilities, enqueueSnackbar, importFacilitiesMutation, showConfirmDialog]);

  const handleSave = useCallback(async () => {
    try {
      const savedFacilities = await saveFacilitiesMutation.mutateAsync(groupsToRows(groups));

      const nextGroups = normalizeGroupOrders(rowsToGroups(savedFacilities));
      setGroups(nextGroups);
      setIsDirty(false);
      setDeletedFacilitiesState(null);
      if (showActions) {
        enqueueSnackbar('Structured facilities saved', { variant: 'success' });
      }
      return true;
    } catch (saveError) {
      enqueueSnackbar(`Error saving facilities: ${(saveError as Error).message}`, { variant: 'error' });
      return false;
    }
  }, [enqueueSnackbar, groups, saveFacilitiesMutation, showActions]);

  const handleCancel = useCallback(() => {
    setGroups(normalizeGroupOrders(rowsToGroups(associatedFacilities ?? [])));
    setIsDirty(false);
    setDeletedFacilitiesState(null);
  }, [associatedFacilities]);

  const handleDeleteGroup = useCallback(
    (groupCode: string) => {
      const targetGroupIndex = groups.findIndex((group) => group.group.code === groupCode);
      const targetGroup = groups[targetGroupIndex];

      if (!targetGroup) {
        return;
      }

      setDeletedFacilitiesState({
        fallbackIndex: targetGroupIndex,
        message: `${targetGroup.group.name} group deleted`,
        groups: cloneGroups(groups),
      });
      setNextGroups((currentGroups) => currentGroups.filter((group) => group.group.code !== groupCode));
    },
    [groups, setNextGroups],
  );

  const handleDeleteFacility = useCallback(
    (facilityCode: string) => {
      const targetGroup = groups.find((group) => group.facilities.some((facility) => facility.code === facilityCode));
      const targetFacility = targetGroup?.facilities.find((facility) => facility.code === facilityCode);
      const targetFacilityIndex = targetGroup?.facilities.findIndex((facility) => facility.code === facilityCode) ?? -1;

      if (!targetFacility) {
        return;
      }

      setDeletedFacilitiesState({
        anchorGroupCode: targetGroup?.group.code,
        facilityIndex: targetFacilityIndex,
        fallbackIndex: groups.findIndex((group) => group.group.code === targetGroup?.group.code),
        message: 'Facility deleted',
        groups: cloneGroups(groups),
      });
      setNextGroups((currentGroups) =>
        currentGroups.map((group) => ({
          ...group,
          facilities: group.facilities.filter((facility) => facility.code !== facilityCode),
        })),
      );
    },
    [groups, setNextGroups],
  );

  const handleUndoDelete = useCallback(() => {
    if (!deletedFacilitiesState) {
      return;
    }

    setGroups(deletedFacilitiesState.groups);
    setIsDirty(hasFacilityChanges(deletedFacilitiesState.groups, associatedFacilities));
    setDeletedFacilitiesState(null);
  }, [associatedFacilities, deletedFacilitiesState]);

  const renderDeletedFacilitiesAlert = useCallback(() => {
    if (!deletedFacilitiesState) {
      return null;
    }

    return (
      <Alert
        severity="warning"
        action={
          <Stack direction="row" spacing={1}>
            <Button color="inherit" size="small" startIcon={<UndoIcon />} onClick={handleUndoDelete}>
              Undo
            </Button>
            <IconButton color="inherit" size="small" onClick={() => setDeletedFacilitiesState(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        {deletedFacilitiesState.message}
      </Alert>
    );
  }, [deletedFacilitiesState, handleUndoDelete]);

  const handleToggleCollapsed = useCallback((groupCode: string) => {
    setCollapsedGroupCodes((currentGroupCodes) => {
      const nextGroupCodes = new Set(currentGroupCodes);

      if (nextGroupCodes.has(groupCode)) {
        nextGroupCodes.delete(groupCode);
      } else {
        nextGroupCodes.add(groupCode);
      }

      return nextGroupCodes;
    });
  }, []);

  const handleUpdateGroupName = useCallback(
    (groupCode: string, name: string) => {
      setNextGroups((currentGroups) =>
        currentGroups.map((group) => {
          if (group.group.code !== groupCode) {
            return group;
          }

          const updatedGroup = {
            ...group.group,
            name,
          };

          return {
            group: updatedGroup,
            facilities: group.facilities.map((facility) => ({
              ...facility,
              catalogGroup: updatedGroup,
            })),
          };
        }),
      );
    },
    [setNextGroups],
  );

  const handleUpdateFacilityName = useCallback(
    (facilityCode: string, name: string) => {
      setNextGroups((currentGroups) =>
        currentGroups.map((group) => ({
          ...group,
          facilities: group.facilities.map((facility) =>
            facility.code === facilityCode
              ? {
                  ...facility,
                  name,
                }
              : facility,
          ),
        })),
      );
    },
    [setNextGroups],
  );

  const handleAddFacility = useCallback(
    (groupCode: string, facility: AccommodationService.AdminFacility) => {
      setDeletedFacilitiesState(null);
      setNextGroups((currentGroups) => {
        const existingGroup = currentGroups.find((group) => group.group.code === groupCode);

        if (!existingGroup) {
          if (!facility.catalogGroup) {
            enqueueSnackbar(`Facility ${facility.code} is missing a catalog group`, { variant: 'error' });
            return currentGroups;
          }

          return [
            ...currentGroups,
            {
              group: facility.catalogGroup,
              facilities: [buildFacilityRow(facility.catalogGroup, facility, [])],
            },
          ];
        }

        return currentGroups.map((group) => {
          if (group.group.code !== groupCode) {
            return group;
          }

          return {
            ...group,
            facilities: [...group.facilities, buildFacilityRow(group.group, facility, group.facilities)],
          };
        });
      });
    },
    [enqueueSnackbar, setNextGroups],
  );

  const handleAddGroup = useCallback(
    (group: AccommodationService.AdminCatalogGroup) => {
      setDeletedFacilitiesState(null);
      setNextGroups((currentGroups) => {
        if (currentGroups.some((currentGroup) => currentGroup.group.code === group.code)) {
          return currentGroups;
        }

        return [
          {
            group,
            facilities: [],
          },
          ...currentGroups,
        ];
      });
    },
    [setNextGroups],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const isGroupDrag = activeId.startsWith('group:');
      const isFacilityDrag = activeId.startsWith('facility:') && overId.startsWith('facility:');

      if (isGroupDrag) {
        setNextGroups((currentGroups) => {
          const oldIndex = currentGroups.findIndex((group) => group.group.code === getCodeFromSortableId(activeId));
          const newIndex = overId.startsWith('group:')
            ? currentGroups.findIndex((group) => group.group.code === getCodeFromSortableId(overId))
            : currentGroups.findIndex((group) =>
                group.facilities.some((facility) => facility.code === getCodeFromSortableId(overId)),
              );

          return oldIndex >= 0 && newIndex >= 0
            ? resetGroupOrdersByPosition(arrayMove(currentGroups, oldIndex, newIndex))
            : currentGroups;
        });
        return;
      }

      if (isFacilityDrag) {
        setNextGroups((currentGroups) => {
          const activeFacilityCode = getCodeFromSortableId(activeId);
          const overFacilityCode = getCodeFromSortableId(overId);
          const activeGroupIndex = currentGroups.findIndex((group) =>
            group.facilities.some((facility) => facility.code === activeFacilityCode),
          );
          const overGroupIndex = currentGroups.findIndex((group) =>
            group.facilities.some((facility) => facility.code === overFacilityCode),
          );

          if (activeGroupIndex < 0 || activeGroupIndex !== overGroupIndex) {
            return currentGroups;
          }

          const activeGroup = currentGroups[activeGroupIndex];
          const oldIndex = activeGroup.facilities.findIndex((facility) => facility.code === activeFacilityCode);
          const newIndex = activeGroup.facilities.findIndex((facility) => facility.code === overFacilityCode);

          return currentGroups.map((group, index) =>
            index === activeGroupIndex
              ? {
                  ...activeGroup,
                  facilities:
                    oldIndex >= 0 && newIndex >= 0
                      ? resetFacilityOrdersByPosition(arrayMove(activeGroup.facilities, oldIndex, newIndex))
                      : activeGroup.facilities,
                }
              : group,
          );
        });
      }
    },
    [setNextGroups],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading structured facilities: {error.message}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          startIcon={<InfoOutlinedIcon />}
          variant="outlined"
          onClick={handleImport}
          disabled={importFacilitiesMutation.isPending || saveFacilitiesMutation.isPending}
        >
          Import facilities
        </Button>
      </Stack>

      <Card variant="outlined">
        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography variant="h6">Facilities</Typography>
          <Alert severity="info">
            Facilities are imported from the mapped accommodation property. You can add, remove, edit, and reorder them
            here; after saving, changes update svc-offer and directly affect the Customer Platform display. Please fill
            in both Facilities and Structured Facilities so users on both sides of the A/B test can see the correct
            facilities experience, and so the legacy Facilities content remains available as a fallback.
          </Alert>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ApartmentIcon color="action" />
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Mapped accommodation
                </Typography>
                <Typography variant="subtitle2">{mappedPropertyLabel}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              {mappedPropertyId && (
                <Button
                  href={`/accommodation/properties/${mappedPropertyId}/facilities`}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  size="small"
                >
                  View mapping
                </Button>
              )}
              <Button
                startIcon={<VisibilityOutlinedIcon />}
                size="small"
                variant="outlined"
                onClick={() => setIsPreviewOpen(true)}
              >
                Preview customer view
              </Button>
            </Stack>
          </Stack>

          {isFetching && <Alert severity="info">Refreshing facilities...</Alert>}

          <AddGroupRow groups={availableApprovedGroups} onAddGroup={handleAddGroup} />

          {groups.length === 0 && deletedFacilitiesState ? (
            renderDeletedFacilitiesAlert()
          ) : groups.length === 0 ? (
            <Alert severity="warning">No structured facilities are associated with this offer.</Alert>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={groups.map((group) => getGroupSortableId(group.group.code))}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={1}>
                  {groups.map((group, index) => (
                    <React.Fragment key={group.group.code}>
                      {!deletedFacilitiesState?.anchorGroupCode &&
                        deletedFacilitiesState?.fallbackIndex === index &&
                        renderDeletedFacilitiesAlert()}
                      <SortableGroup
                        group={group}
                        isCollapsed={collapsedGroupCodes.has(group.group.code)}
                        onToggleCollapsed={handleToggleCollapsed}
                        onDeleteGroup={handleDeleteGroup}
                        onDeleteFacility={handleDeleteFacility}
                        deletedFacilitiesState={deletedFacilitiesState}
                        onDismissDelete={() => setDeletedFacilitiesState(null)}
                        onUndoDelete={handleUndoDelete}
                        onUpdateGroupName={handleUpdateGroupName}
                        onUpdateFacilityName={handleUpdateFacilityName}
                        availableFacilities={availableApprovedFacilitiesByGroup.get(group.group.code) ?? []}
                        onAddFacility={handleAddFacility}
                      />
                    </React.Fragment>
                  ))}
                  {deletedFacilitiesState &&
                    !deletedFacilitiesState.anchorGroupCode &&
                    deletedFacilitiesState.fallbackIndex >= groups.length &&
                    renderDeletedFacilitiesAlert()}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Stack>
      </Card>

      {showActions && (
        <Stack direction="row" spacing={2}>
          <Button variant="contained" disabled={!isDirty || saveFacilitiesMutation.isPending} onClick={handleSave}>
            Save
          </Button>
          <Button disabled={!isDirty || saveFacilitiesMutation.isPending} onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      )}

      <CustomerPreviewDialog open={isPreviewOpen} groups={groups} onClose={() => setIsPreviewOpen(false)} />
    </Stack>
  );
}
